import { isMobileDevice, site } from '@panel/commons/helpers';

const siteId = site.id;
const isMobile = isMobileDevice();

const formatCep = value => {
  if (!value && value !== '') return null;
  const matchFormat = value.replace(/\s/g, '').match(/\d{3,5}/g);
  const result = matchFormat ? matchFormat.join('-').substr(0, 9) : null;
  return /[0-9]{5}-?[\d]{3}/.test(result) ? result : value;
};

const tabsConfig = [
  {
    label: 'Preencher com CEP',
    id: 1
  },
  {
    label: isMobile ? 'Endereço' : 'Preencher com endereço',
    id: 2
  }
];

const isAnExcludePortaltoGetLatLng = ['PLEC', 'CAPA'].includes(siteId);
const hasCep = ['IWBR', 'WIBR', 'ICBR'].includes(siteId);
const addressSeparator = ['IWBR', 'WIBR', 'ICBR'].includes(siteId) ? '-' : ',';
const showSubDistrict = ['24MX', 'IWBR', 'WIBR', 'ICBR', 'UBPE', 'ZPAR'].includes(siteId);
const countriesIds = {
  '24MX': { countryCode: '18', gmapCode: 'mx' },
  DVPE: { countryCode: '11', gmapCode: 'pe' },
  UBPE: { countryCode: '111', gmapCode: 'pe' },
  PLEC: { countryCode: '9', gmapCode: 'ec' },
  CAPA: { countryCode: '20', gmapCode: 'pa' },
  LLVE: { countryCode: '13', gmapCode: 've' },
  ZPAR: { countryCode: '1', gmapCode: 'ar' },
  ICBR: { countryCode: '2', gmapCode: 'br' },
  IWBR: { countryCode: '2', gmapCode: 'br' },
  WIBR: { countryCode: '2', gmapCode: 'br' },
  VAMX: { countryCode: '36', gmapCode: 'mx' }
};

// Configuration for selects based on portal exepct BR that has its own form view
const selectsConfigByPortal = {
  '24MX': {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: false,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: true
    }
  },
  VAMX: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: false,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: true
    }
  },
  ZPAR: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: false,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: true
    }
  },
  CAPA: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: true,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: false
    }
  },
  UBPE: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: true,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: true
    }
  },
  PLEC: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: true,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: false
    }
  },
  DVPE: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: true,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: true
    }
  },
  DEFAULT: {
    provinceConfig: {
      mandatory: true,
      visible: true
    },
    cityConfig: {
      mandatory: true,
      visible: true
    },
    zoneConfig: {
      mandatory: false,
      visible: true
    },
    subZoneConfig: {
      mandatory: false,
      visible: true
    },
    postalCodeConfig: {
      mandatory: false,
      visible: true
    }
  }
};

/**
 * Extracts the `long_name` of the postal code from Google Maps Geocoding API results.
 *
 * @param {Array<Object>} geocodeResults - Array of geocoding result objects, each potentially containing address components.
 * @returns {(string|null)} The `long_name` of the postal code if found, otherwise `null`.
 */
function getPostalCodeLongName(geocodeResults) {
  // Verificar si el parámetro es un array y no está vacío
  if (!Array.isArray(geocodeResults) || geocodeResults.length === 0) {
    return null;
  }

  // Iterar a través de cada resultado
  for (const result of geocodeResults) {
    // Verificar si el resultado tiene address_components
    if (result.address_components && Array.isArray(result.address_components)) {
      // Buscar el componente con tipo 'postal_code'
      const postalCodeComponent = result.address_components.find(
        component => component.types && component.types.includes('postal_code')
      );

      // Si se encuentra, procesar el long_name
      if (postalCodeComponent && postalCodeComponent.long_name) {
        let postalCode = postalCodeComponent.long_name;

        // hay casos en los que de la API de Google Maps no se devuelve el CEP completo
        if (!postalCode.includes('-')) {
          postalCode += '-000';
        }

        return postalCode;
      }
    }
  }

  // Si no se encuentra postal_code en ningún resultado
  return null;
}

const isValidFormatCep = value => {
  // Regex que permite números y máximo un guión medio
  const regex = /^[0-9]*-?[0-9]*$/;

  // Verificar que coincida con el patrón básico
  if (!regex.test(value)) {
    return false;
  }

  // Verificar que haya máximo un guión
  const dashCount = (value.match(/-/g) || []).length;
  if (dashCount > 1) {
    return false;
  }
  return true;
};

export {
  formatCep,
  tabsConfig,
  hasCep,
  addressSeparator,
  countriesIds,
  showSubDistrict,
  selectsConfigByPortal,
  getPostalCodeLongName,
  isAnExcludePortaltoGetLatLng,
  isValidFormatCep
};
